/**
 * L2 LLM Audit v2 — Claude Sonnet 4.6 per-seed chunked.
 *
 * Prompt: thematic_prompts.step='scn_audit' version=2 (SRO 5 warstw).
 * Chunking: per-seed (14 calls dla sushi). Każdy seed dostaje swój context
 * i zwraca JSON per-seed score. Orchestrator agreguje do global.
 *
 * Model: claude-sonnet-4-6, temperature 0.1 (deterministic), maxTokens 8000.
 * Cost: ~$0.05-0.10 per mapa (5× tańszy niż Opus, ~3× szybszy).
 * Zmiana z Opus→Sonnet 2026-04-17: Opus obcinało odpowiedź na 4K + zajmowało
 * 2-3 minuty per seed. Sonnet starcza do SRO JSON output i radzi sobie <30s/seed.
 */

import { supabase } from "@/integrations/supabase/client";
import { loadPrompt, fillTemplate } from "@/services/llm/promptLoader";
import { claudeProvider } from "@/services/llm/claudeProvider";
import { parseLLMJSON } from "@/services/llm";
import { auditLog } from "@/services/contentEngine/contentLogger";
const logPublic = auditLog.public;
const logDebug = auditLog.debug;
const logError = auditLog.error;
import type { AuditIssue, AuditVerdict } from "@/types/thematicMaps";

const PREFIX = "[AUDIT-L2-LLM]";

export interface SroLayerScore {
  score: number;
  max: number;
  verdict: AuditVerdict;
  issues: AuditIssue[];
}

export interface PerSeedResult {
  seed_id: string;
  seed_name: string;
  seed_verdict: AuditVerdict;
  seed_total_score: number;
  sro_layers: {
    macrosemantics: SroLayerScore;
    microsemantics: SroLayerScore;
    technical_eligibility: SroLayerScore;
    trust_calibration: SroLayerScore;
    query_semantics: SroLayerScore;
  };
  strategic_recommendations: string[];
}

export interface L2LLMResult {
  score: number;
  max: number;
  verdict: AuditVerdict;
  per_seed: PerSeedResult[];
  sro_layers_aggregate: {
    macrosemantics: SroLayerScore;
    microsemantics: SroLayerScore;
    technical_eligibility: SroLayerScore;
    trust_calibration: SroLayerScore;
    query_semantics: SroLayerScore;
  };
  critical_findings: AuditIssue[];
  strategic_recommendations: string[];
  cost_usd: number;
  tokens_in: number;
  tokens_out: number;
  duration_ms: number;
  skipped?: boolean;
  skip_reason?: string;
}

type LogCb = (
  type: "info" | "success" | "error" | "stage",
  message: string,
  debug?: Record<string, unknown>,
) => void;

function emptyLayer(): SroLayerScore {
  return { score: 20, max: 20, verdict: "pass" as AuditVerdict, issues: [] };
}

function aggregateLayers(perSeed: PerSeedResult[]): L2LLMResult["sro_layers_aggregate"] {
  const layers = ["macrosemantics", "microsemantics", "technical_eligibility", "trust_calibration", "query_semantics"] as const;
  const agg = {
    macrosemantics: emptyLayer(),
    microsemantics: emptyLayer(),
    technical_eligibility: emptyLayer(),
    trust_calibration: emptyLayer(),
    query_semantics: emptyLayer(),
  };

  if (perSeed.length === 0) return agg;

  for (const layer of layers) {
    let sum = 0;
    const issues: AuditIssue[] = [];
    for (const s of perSeed) {
      const l = s.sro_layers?.[layer];
      if (l) {
        sum += l.score;
        issues.push(...(l.issues || []));
      }
    }
    const avg = sum / perSeed.length;
    const verdict: AuditVerdict = avg >= 16 ? "pass" : avg >= 10 ? "warn" : "fail";
    agg[layer] = {
      score: Math.round(avg),
      max: 20,
      verdict,
      issues: issues
        .sort((a, b) => {
          const order = { error: 0, warning: 1, info: 2, ok: 3 } as const;
          return order[a.severity] - order[b.severity];
        })
        .slice(0, 15),
    };
  }
  return agg;
}

export async function callLlmScnAuditV2(
  mapId: string,
  options: {
    onLog?: LogCb;
  } = {},
): Promise<L2LLMResult> {
  const t0 = Date.now();

  // Load prompt
  const promptDef = await loadPrompt("scn_audit");
  if (!promptDef) {
    return {
      score: 0,
      max: 20,
      verdict: "warn",
      per_seed: [],
      sro_layers_aggregate: aggregateLayers([]),
      critical_findings: [],
      strategic_recommendations: [],
      cost_usd: 0,
      tokens_in: 0,
      tokens_out: 0,
      duration_ms: Date.now() - t0,
      skipped: true,
      skip_reason: "Prompt scn_audit v2 nie jest aktywny w DB",
    };
  }

  // Load mapa + seeds + nodes + relations
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: map } = await (supabase as any)
    .from("thematic_maps")
    .select("id, macro_statement")
    .eq("id", mapId)
    .maybeSingle();

  const macroStatement = (map as { macro_statement: string | null } | null)?.macro_statement ?? "";

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: seedsData } = await (supabase as any)
    .from("thematic_seeds")
    .select("id, name, priority, cluster_type, intent_family")
    .eq("map_id", mapId)
    .or("status.is.null,status.eq.active") // M18: nie wydajemy LLM $ na non-active seedy
    .order("priority", { ascending: true });
  const seeds = (seedsData || []) as Array<{
    id: string;
    name: string;
    priority: number | null;
    cluster_type: string | null;
    intent_family: unknown;
  }>;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: nodesData } = await (supabase as any)
    .from("thematic_nodes")
    .select(
      "id, slug, seed_id, document_role, primary_intent, primary_entity_raw, search_volume, demand_tier, paa_preview, matched_wp_post_id",
    )
    .eq("map_id", mapId);
  const nodes = (nodesData || []) as Array<{
    id: string;
    slug: string;
    seed_id: string;
    document_role: string;
    primary_intent: string;
    primary_entity_raw: string | null;
    search_volume: number | null;
    demand_tier: string | null;
    paa_preview: unknown;
    matched_wp_post_id: string | null;
  }>;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: relsData } = await (supabase as any)
    .from("thematic_node_relations")
    .select("from_node_id, to_node_id, relation_type, weight")
    .eq("map_id", mapId);
  const relations = (relsData || []) as Array<{
    from_node_id: string;
    to_node_id: string;
    relation_type: string;
    weight: number | null;
  }>;

  const slugById = new Map<string, string>();
  for (const n of nodes) slugById.set(n.id, n.slug);

  // Incoming count per node
  const incoming = new Map<string, number>();
  for (const r of relations) incoming.set(r.to_node_id, (incoming.get(r.to_node_id) ?? 0) + 1);

  const totalSeeds = seeds.length;
  const totalNodes = nodes.length;

  const modelName = promptDef.modelId || "claude-sonnet-5";
  options.onLog?.(
    "stage",
    `▶ L2 LLM audit (${modelName}): ${totalSeeds} seedów × chunked · max 8K tokenów/seed`,
    { model: modelName, total_seeds: totalSeeds, prompt_chars: promptDef.template.length },
  );

  const perSeed: PerSeedResult[] = [];
  let totalCost = 0;
  let totalTokIn = 0;
  let totalTokOut = 0;
  let failedCount = 0;

  for (let i = 0; i < seeds.length; i++) {
    const seed = seeds[i];
    const seedT0 = Date.now();
    const seedNodes = nodes
      .filter((n) => n.seed_id === seed.id)
      .map((n) => ({
        slug: n.slug,
        document_role: n.document_role,
        primary_intent: n.primary_intent,
        primary_entity: n.primary_entity_raw,
        search_volume: n.search_volume ?? 0,
        demand_tier: n.demand_tier,
        paa_preview: Array.isArray(n.paa_preview) ? n.paa_preview : [],
        matched_wp_post: !!n.matched_wp_post_id,
        incoming_count: incoming.get(n.id) ?? 0,
      }));

    // Relations sample: wszystkie intra-seed + top 20 cross-seed z tego seeda
    const seedNodeIds = new Set(nodes.filter((n) => n.seed_id === seed.id).map((n) => n.id));
    const intraRels = relations.filter(
      (r) => seedNodeIds.has(r.from_node_id) && seedNodeIds.has(r.to_node_id),
    );
    const crossRels = relations
      .filter(
        (r) =>
          (seedNodeIds.has(r.from_node_id) && !seedNodeIds.has(r.to_node_id)) ||
          (!seedNodeIds.has(r.from_node_id) && seedNodeIds.has(r.to_node_id)),
      )
      .slice(0, 20);
    const allRels = [...intraRels, ...crossRels]
      .slice(0, 50)
      .map((r) => ({
        from_slug: slugById.get(r.from_node_id) || "",
        to_slug: slugById.get(r.to_node_id) || "",
        relation_type: r.relation_type,
        weight: r.weight ?? 0,
      }));

    const compiled = fillTemplate(promptDef.template, {
      map_macro_statement: macroStatement,
      seed_name: seed.name,
      seed_cluster_type: seed.cluster_type ?? "core",
      seed_intent_family: JSON.stringify(seed.intent_family ?? []),
      seed_priority: String(seed.priority ?? 3),
      nodes_chunk: JSON.stringify(seedNodes),
      relations_sample: JSON.stringify(allRels),
      map_total_nodes: String(totalNodes),
      map_total_seeds: String(totalSeeds),
    });

    options.onLog?.(
      "info",
      `  ⏳ [${i + 1}/${totalSeeds}] "${seed.name}" (${seedNodes.length} nodów, ${allRels.length} relacji, prompt ${(compiled.length / 1024).toFixed(1)}KB) → ${modelName}...`,
    );

    try {
      // Retry na 502/504 (Bad Gateway / Gateway Timeout z Coolify/Traefik albo Anthropic edge).
      // Duze prompty (~19KB) + claude-sonnet-4-6 to 30-60s per call, czasem gateway drop.
      // 2 retries z exp backoff (3s, 6s). Bledy non-gateway — no retry, rethrow.
      let response: Awaited<ReturnType<typeof claudeProvider.call>> | null = null;
      let lastErr: unknown = null;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          response = await claudeProvider.call(compiled, {
            model: modelName,
            temperature: promptDef.temperature ?? 0.1,
            maxTokens: 8000,
          });
          break;
        } catch (e) {
          lastErr = e;
          const msg = e instanceof Error ? e.message : String(e);
          const isGateway =
            /\bBad Gateway\b/i.test(msg) ||
            /\bGateway Timeout\b/i.test(msg) ||
            /\b50[24]\b/.test(msg);
          if (!isGateway || attempt === 2) throw e;
          const backoffMs = (attempt + 1) * 3000;
          options.onLog?.(
            "info",
            `  ⟳ [${i + 1}/${totalSeeds}] "${seed.name}" gateway error (${msg.slice(0, 60)}...) — retry ${attempt + 1}/2 za ${backoffMs / 1000}s`,
          );
          await new Promise((r) => setTimeout(r, backoffMs));
        }
      }
      if (!response) throw lastErr ?? new Error("no response");
      totalCost += response.costUsd ?? 0;
      totalTokIn += response.tokensInput;
      totalTokOut += response.tokensOutput;

      const seedDur = ((Date.now() - seedT0) / 1000).toFixed(1);
      // Truncation detection — strip trailing markdown fences ("```" / "```json")
      // i whitespace zeby nie flagowac kompletnych JSON-ow zawinietych w markdown.
      // Sonnet czesto wraca: "...} ] ```" → to KOMPLETNY JSON, nie obciety.
      const cleanTail = response.text
        .trim()
        .replace(/```(?:json)?\s*$/i, "")
        .trim();
      const truncated = cleanTail.length > 0
        && !cleanTail.endsWith("}")
        && !cleanTail.endsWith("]");

      if (truncated) {
        options.onLog?.(
          "error",
          `  ⚠ [${i + 1}/${totalSeeds}] "${seed.name}" — odpowiedź ucięta (${response.tokensOutput} tok out / max 8K). Ostatnie znaki: "${response.text.slice(-50)}"`,
          { tokens_output: response.tokensOutput, last_chars: response.text.slice(-200) },
        );
      }

      const parsed = parseLLMJSON<Omit<PerSeedResult, "seed_id">>(response.text);
      const safeLayers = parsed.sro_layers ?? {
        macrosemantics: emptyLayer(),
        microsemantics: emptyLayer(),
        technical_eligibility: emptyLayer(),
        trust_calibration: emptyLayer(),
        query_semantics: emptyLayer(),
      };
      perSeed.push({
        seed_id: seed.id,
        seed_name: seed.name,
        seed_verdict: parsed.seed_verdict ?? "pass",
        seed_total_score: parsed.seed_total_score ?? 0,
        sro_layers: safeLayers,
        strategic_recommendations: parsed.strategic_recommendations ?? [],
      });

      // Zlicz issues per severity (agregat z 5 warstw)
      let err = 0, warn = 0, info = 0;
      for (const layer of Object.values(safeLayers)) {
        for (const iss of layer?.issues ?? []) {
          if (iss.severity === "error") err++;
          else if (iss.severity === "warning") warn++;
          else if (iss.severity === "info") info++;
        }
      }

      const layerScores = [
        `macro=${safeLayers.macrosemantics?.score ?? 0}`,
        `micro=${safeLayers.microsemantics?.score ?? 0}`,
        `tech=${safeLayers.technical_eligibility?.score ?? 0}`,
        `trust=${safeLayers.trust_calibration?.score ?? 0}`,
        `query=${safeLayers.query_semantics?.score ?? 0}`,
      ].join("/");

      options.onLog?.(
        "success",
        `  ✓ [${i + 1}/${totalSeeds}] "${seed.name}" → ${parsed.seed_total_score ?? 0}/100 (${parsed.seed_verdict ?? "?"}) · warstwy ${layerScores} · issues ERR${err}/WARN${warn}/INFO${info} · ${seedDur}s · ${response.tokensOutput}tok · $${(response.costUsd ?? 0).toFixed(4)}`,
        {
          seed_id: seed.id,
          seed_name: seed.name,
          total_score: parsed.seed_total_score,
          verdict: parsed.seed_verdict,
          layer_scores: safeLayers,
          issues_count: { error: err, warning: warn, info },
          duration_s: seedDur,
          tokens: { in: response.tokensInput, out: response.tokensOutput },
          cost_usd: response.costUsd,
        },
      );
      logDebug(PREFIX, `Seed ${i + 1}/${totalSeeds} done: ${seed.name} score=${parsed.seed_total_score} dur=${seedDur}s`);
    } catch (err) {
      failedCount++;
      const msg = err instanceof Error ? err.message : String(err);
      const seedDur = ((Date.now() - seedT0) / 1000).toFixed(1);
      logError(PREFIX, `Seed ${seed.name} failed: ${msg}`);
      options.onLog?.("error", `  ✗ [${i + 1}/${totalSeeds}] "${seed.name}" FAILED po ${seedDur}s: ${msg}`, { error: msg });
      // Continue — kontynuuj pozostałe seedy
    }
  }

  const aggregate = aggregateLayers(perSeed);
  const avgScore = perSeed.length > 0
    ? perSeed.reduce((s, p) => s + p.seed_total_score, 0) / perSeed.length
    : 0;
  const globalLLMScore = Math.round(avgScore * 20 / 100); // scale 0-100 → 0-20
  const globalVerdict: AuditVerdict =
    globalLLMScore >= 16 ? "pass" : globalLLMScore >= 10 ? "warn" : "fail";

  // Krytyczne findings — wszystkie ERRORS z wszystkich per-seed
  const criticalFindings: AuditIssue[] = [];
  for (const s of perSeed) {
    for (const layerName of ["macrosemantics", "microsemantics", "technical_eligibility", "trust_calibration", "query_semantics"] as const) {
      const l = s.sro_layers?.[layerName];
      if (l?.issues) {
        for (const issue of l.issues) {
          if (issue.severity === "error") {
            criticalFindings.push({ ...issue, dimension: `${layerName}:${s.seed_name}` });
          }
        }
      }
    }
  }

  const globalRecs = perSeed.flatMap((s) => s.strategic_recommendations).slice(0, 15);

  const duration_ms = Date.now() - t0;
  const avgSeedScore = perSeed.length > 0
    ? perSeed.reduce((s, p) => s + p.seed_total_score, 0) / perSeed.length
    : 0;
  logPublic(
    PREFIX,
    `L2 LLM done ${duration_ms}ms: ${perSeed.length}/${totalSeeds} seedów, score=${globalLLMScore}/20, cost=$${totalCost.toFixed(4)}`,
  );
  options.onLog?.(
    "stage",
    `✓ L2 LLM done ${(duration_ms / 1000).toFixed(1)}s · ${perSeed.length}/${totalSeeds} seedów (fail ${failedCount}) · avg seed ${avgSeedScore.toFixed(1)}/100 → global ${globalLLMScore}/20 (${globalVerdict}) · ${criticalFindings.length} errorów · tok ${totalTokIn}→${totalTokOut} · cost $${totalCost.toFixed(4)}`,
    {
      seeds_audited: perSeed.length,
      seeds_failed: failedCount,
      avg_seed_score: avgSeedScore,
      global_score: globalLLMScore,
      global_verdict: globalVerdict,
      critical_findings_count: criticalFindings.length,
      cost_usd: totalCost,
      tokens_in: totalTokIn,
      tokens_out: totalTokOut,
      duration_s: (duration_ms / 1000).toFixed(1),
    },
  );

  return {
    score: globalLLMScore,
    max: 20,
    verdict: globalVerdict,
    per_seed: perSeed,
    sro_layers_aggregate: aggregate,
    critical_findings: criticalFindings,
    strategic_recommendations: globalRecs,
    cost_usd: totalCost,
    tokens_in: totalTokIn,
    tokens_out: totalTokOut,
    duration_ms,
  };
}

export { PREFIX as L2_LLM_PREFIX };
